import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import CountUp from '../ui/CountUp';
import { cn } from '../../lib/utils';

const TONES = {
  primary: {
    icon: 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300',
    ring: 'ring-primary-100 dark:ring-primary-900/40',
  },
  success: {
    icon: 'bg-success-50 text-success-600 dark:bg-success-900/30 dark:text-success-300',
    ring: 'ring-success-100 dark:ring-success-900/40',
  },
  danger: {
    icon: 'bg-danger-50 text-danger-600 dark:bg-danger-900/30 dark:text-danger-300',
    ring: 'ring-danger-100 dark:ring-danger-900/40',
  },
  warning: {
    icon: 'bg-warning-50 text-warning-600 dark:bg-warning-900/30 dark:text-warning-300',
    ring: 'ring-warning-100 dark:ring-warning-900/40',
  },
  neutral: {
    icon: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200',
    ring: 'ring-neutral-100 dark:ring-neutral-800',
  },
};

const formatValue = (value, format) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (format === 'currency') {
    return new Intl.NumberFormat('ar-AE', { maximumFractionDigits: 2 }).format(value);
  }
  if (format === 'percent') {
    return `${Number(value).toFixed(1)}%`;
  }
  return new Intl.NumberFormat('ar-AE').format(value);
};

/**
 * StatsCard — one KPI tile used in Stats Row.
 *
 * Props:
 *   label          string — small label on top
 *   value          number|string — main value
 *   format         'number'|'currency'|'percent' (default number)
 *   suffix         string — e.g. "د.إ"
 *   icon           Lucide icon
 *   tone           'primary'|'success'|'danger'|'warning'|'neutral' (default primary)
 *   trend          number — percent change (+4.2 / -1.1 / 0)
 *   trendLabel     string — context for trend e.g. "مقارنة بالشهر الماضي"
 *   loading        boolean — shows skeleton
 *   footer         ReactNode — optional small footer (e.g. link)
 */
const StatsCard = ({
  label,
  value,
  format = 'number',
  suffix = '',
  icon: Icon = null,
  tone = 'primary',
  trend = null,
  trendLabel = '',
  loading = false,
  footer = null,
  className = '',
}) => {
  const t = TONES[tone] || TONES.primary;
  const numeric = typeof value === 'number';

  const trendTone =
    trend === null || trend === undefined
      ? null
      : trend > 0
      ? 'text-success-600 dark:text-success-400'
      : trend < 0
      ? 'text-danger-600 dark:text-danger-400'
      : 'text-neutral-500 dark:text-neutral-400';

  const TrendIcon =
    trend === null || trend === undefined
      ? null
      : trend > 0
      ? TrendingUp
      : trend < 0
      ? TrendingDown
      : Minus;

  return (
    <div
      className={cn(
        'group relative rounded-lg bg-white dark:bg-neutral-900',
        'border border-neutral-200 dark:border-neutral-800',
        'shadow-sm hover:shadow-md',
        'px-5 py-4 transition-[box-shadow,transform] duration-200 ease-out',
        'hover:-translate-y-0.5 motion-reduce:transform-none motion-reduce:transition-none',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 truncate">
            {label}
          </p>
          <div className="mt-2 flex items-baseline gap-1.5">
            {loading ? (
              <span className="inline-block h-7 w-24 rounded-md bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
            ) : (
              <>
                <span
                  className="font-mono text-2xl md:text-[28px] font-semibold text-neutral-900 dark:text-neutral-50 tabular-nums leading-none"
                  style={{ fontVariantNumeric: 'tabular-nums slashed-zero' }}
                >
                  {numeric && format !== 'percent' ? (
                    <CountUp value={value} />
                  ) : (
                    formatValue(value, format)
                  )}
                </span>
                {suffix && (
                  <span className="text-sm text-neutral-500 dark:text-neutral-400 font-sans">{suffix}</span>
                )}
              </>
            )}
          </div>

          {(trend !== null && trend !== undefined) || trendLabel ? (
            <div className="mt-2 flex items-center gap-1.5 text-xs">
              {TrendIcon && (
                <span className={cn('inline-flex items-center gap-0.5 font-semibold', trendTone)}>
                  <TrendIcon size={12} />
                  {Math.abs(Number(trend)).toFixed(1)}%
                </span>
              )}
              {trendLabel && (
                <span className="text-neutral-400 dark:text-neutral-500 truncate">{trendLabel}</span>
              )}
            </div>
          ) : null}
        </div>

        {Icon && (
          <span
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-lg flex-shrink-0 ring-1 ring-inset',
              t.icon,
              t.ring,
            )}
            aria-hidden="true"
          >
            <Icon size={20} />
          </span>
        )}
      </div>

      {footer && <div className="mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-800">{footer}</div>}
    </div>
  );
};

export default StatsCard;
